<template>
  <div class="footnote-display mt-4">
    <spinner v-if="running"></spinner>
    <div class="row">
      <div class="col-xl-5 text pl-sm-2 mb-3 mb-lg-0">
        <div class="raised-frame">
          <div class="d-flex justify-content-between bg-background rounded-top">
            <h2 class="px-2 my-0 py-1">{{ hebrew ? "טקסט" : "Text" }}</h2>
            <router-link
              class="search-link text-body mb-1 mx-1 px-1 rounded"
              to="/"
            >
              {{ hebrew ? "טקסט חדש" : "New text" }}
              <i
                v-if="hebrew"
                class="fa fa-arrow-circle-left"
                aria-hidden="true"
              ></i>
              <i v-else class="fa fa-arrow-circle-right" aria-hidden="true"></i>
            </router-link>
          </div>
          <div
            class="text-col f-narkis bg-white p-3 rounded-bottom searched-text"
          >
            <small
              class="rounded-pill identified-text bg-secondary py-1 px-2 mb-2 d-inline-block"
              v-if="identified"
            >
              {{ identified.book }}
              <span class="rounded-pill d-inline-block text-body bg-white px-2">
                {{ identified.val }}% {{ hebrew ? "התאמה" : "match" }}
              </span>
            </small>
            <p v-html="displayText" @click="textClicked"></p>
          </div>
        </div>
      </div>
      <div class="col-xl-7 results pr-lg-2">
        <div class="raised-frame">
          <div
            class="d-sm-flex justify-content-between bg-background rounded-top p-1"
          >
            <div class="px-2">
              <h2 class="my-0 d-inline">
                {{ hebrew ? "מקבילות" : "Parallels" }}
              </h2>
              <small class="mx-2" id="parallels-count"
                >{{ numResults }}
                {{ hebrew ? "מקבילות נמצאו" : "Result(s)" }}</small
              >
            </div>
            <div>
              <div class="d-inline-block mx-2">
                <set-accuracy />
              </div>
              <b-btn
                v-b-tooltip.hover
                :title="hebrew ? 'מיון תוצאות' : 'Sort Results'"
                variant="link"
                class="px-0 open-meta d-none d-md-inline-block"
                @click="
                  sefariaData = null
                  showSourcePopup = true
                "
              >
                <i class="fas fa-sliders-h"></i>
              </b-btn>
              <download-results class="d-inline-block"></download-results>
            </div>
          </div>
          <div
            v-if="data.length > 0"
            class="text-col bg-white px-3 pb-3 rounded-bottom"
            id="scrollable-area"
          >
            <div
              ref="sourceItem"
              class="my-4 pt-2 p-group position-relative"
              :class="{ 'd-none': subgroup.length === 0 }"
              v-for="(subgroup, j) in parallelResults"
              :key="j"
            >
              <div
                v-for="(item, index) in subgroup"
                :key="index"
                class="item-wrap"
              >
                <div
                  class="d-block px-3 f-narkis matched-text cursor-pointer"
                  :class="{ 'active-src': activeSrcIndex == j }"
                  :id="'match-' + j"
                  v-if="index == 0"
                  @click="findSource(j)"
                >
                  <small class="text-primary position-absolute footnote-num"
                    >[{{ j + 1 }}]</small
                  >
                  <small class="text-muted mb-2 mr-2 d-block">
                    <span
                      v-for="(letter, i) in item.baseMatchedText
                        .replace(/ +(?= )/g, '')
                        .split(' ')"
                      :key="i"
                      class="d-inline-block pr-1"
                      :class="{
                        'first-word': i == 0,
                        'last-word pl-1':
                          i ==
                          item.baseMatchedText
                            .replace(/ +(?= )/g, '')
                            .split(' ').length -
                            1,
                      }"
                    >
                      {{ letter }}
                    </span>
                  </small>
                </div>
                <div
                  class="bg-light border-top-g border-bottom-g d-flex justify-content-between cursor-pointer"
                  :class="hebrew ? 'pr-2  pl-1' : 'pr-1  pl-2'"
                  :dir="hebrew ? 'rtl' : 'ltr'"
                  v-if="index == 0"
                  @click="toggleList(j)"
                >
                  <div
                    class="results-num align-items-center d-flex"
                    v-if="subgroup.length >= 1"
                  >
                    <small v-if="hebrew">
                      נמצאו
                      {{ subgroup.length }}
                      גרסאות מקבילות
                    </small>
                    <small class="text-left" dir="ltr" v-else>
                      {{ subgroup.length }}
                      Parallels found
                    </small>
                  </div>
                  <div class="btns">
                    <b-btn
                      variant="link"
                      class="p-0 open-meta-single d-none d-md-inline-block"
                      @click.prevent.stop="getSingleMetaData(subgroup)"
                    >
                      <span
                        class="text-white rounded-circle d-block m-auto d-block position-relative"
                      >
                        <i class="fas fa-info"></i>
                      </span>
                    </b-btn>
                    <b-btn
                      variant="link"
                      :class="hebrew ? 'pr-4  pl-1' : 'pr-1  pl-4'"
                      class="synopsis-btn py-0"
                      v-if="compareMatches(subgroup)"
                      @click.stop="showSynopsisClicked(subgroup)"
                    >
                      <span class="position-absolute text-white rounded-circle">
                        א|ב
                      </span>
                      <span class="border-bottom">{{
                        hebrew ? "השוואת גרסאות" : "View synopsis"
                      }}</span>
                    </b-btn>
                    <span v-else class="px-1 text-muted">
                      {{
                        hebrew
                          ? "כל הגרסאות זהות"
                          : "All versions are identical"
                      }}</span
                    >
                  </div>
                </div>
                <div
                  class="px-3 py-2 bg-light"
                  :class="{ 'inner-results': index > 0 }"
                >
                  <div class="f-narkis px-3">
                    <span
                      >...{{
                        item.compMatchedText
                          .substring(0, item.compStartChar)
                          .split(" ")
                          .splice(-10)
                          .join(" ")
                      }}</span
                    >
                    <b>{{
                      item.compMatchedText.substring(
                        item.compStartChar,
                        item.compStartChar + item.compTextLength
                      )
                    }}</b>
                    <span>{{
                      item.compMatchedText
                        .substring(
                          item.compStartChar + item.compTextLength,
                          item.compMatchedText.length
                        )
                        .replace(/(([^\s]+\s\s*){10})(.*)/, "$1…")
                    }}</span>
                  </div>
                  <small
                    @click="sourceClicked(item)"
                    class="text-primary cursor-pointer d-block mt-1 px-3"
                    >{{ item.compNameHe }}
                    <span v-if="item.year > 0">({{ item.year }})</span>
                  </small>
                </div>
                <div
                  class="bg-light cursor-pointer text-center"
                  @click="toggleList(j)"
                >
                  <b-btn
                    variant="link"
                    v-if="subgroup.length > 1 && index == 1"
                    class="read-more rounded-circle my-2 text-center p-0"
                  >
                    <i class="fas fa-angle-down"></i>
                  </b-btn>
                  <b-btn
                    variant="link"
                    v-if="subgroup.length > 1 && index == subgroup.length - 1"
                    class="read-less rounded-circle my-2 text-center p-0"
                  >
                    <i class="fas fa-angle-up"></i>
                  </b-btn>
                </div>
              </div>
            </div>
          </div>
          <div class="text-col bg-white rounded-bottom" v-else>
            <div
              class="frame p-3 bg-light m-3 invisible"
              :class="{ 'text-left': !hebrew }"
            >
              {{ hebrew ? "לא נמצאו מקבילות" : "No Parallels found" }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <ChapterPopup
      v-model="showFullSource"
      v-if="showFullSource"
      :src-item="itemForChapter"
    ></ChapterPopup>
    <synopsis v-model="showSynopsis" />
    <source-filter-popup
      v-model="showSourcePopup"
      @selectedBooks="setFilterValues"
      @settings="updateSettings"
      :showEarly="showEarly"
      :hideCanocial="hideShortCanonical"
      :metaData="sefariaData"
    ></source-filter-popup>
    <server-failed-popup
      v-if="failed"
      v-model="failed"
      :msg="serverErrorMessage"
    />
  </div>
</template>

<script>
import axios from "axios"
import { Actions } from "@/store/stateChanges"
import { RunStates } from "@/store/runStates"
import DownloadResults from "@/components/DownloadResults"
import ChapterPopup from "@/components/ChapterPopup"
import SetAccuracy from "@/components/SetAccuracy"
import Synopsis from "@/components/SynopsisResults"
import Spinner from "@/components/spinner"
import SourceFilterPopup from "@/components/SourceFilterPopup"
import ServerFailedPopup from "@/components/ServerFailedPopup"
import metaData from "@/components/MetaDataSefaria.json"
import BenYehudaMetaData from "@/components/MetaDataBY.json"
import _ from "lodash"

function gtag() {
  window.dataLayer.push(arguments)
}

export default {
  name: "FootnoteDisplay",
  components: {
    DownloadResults,
    ChapterPopup,
    Spinner,
    Synopsis,
    ServerFailedPopup,
    SetAccuracy,
    SourceFilterPopup,
  },
  data() {
    return {
      showFullSource: false,
      comparedText: [],
      showLoader: false,
      itemForChapter: {},
      searchedTextArr: [],
      matchedArr: [],
      displayText: "",
      parallelResults: [],
      activeSrcIndex: -1,
      showSynopsis: false,
      sefariaData: [],
      singleObjectClicked: false,
      sourcesToExclude: [],
      sourcesToInclude: [],
      showEarly: false,
      hideShortCanonical: false,
      numResults: 0,
      dictaLibraryBooks: [],
      showSourcePopup: false,
    }
  },
  mounted() {
    if (this.textForApiCall === "") this.$router.push({ name: "Search" })
    else this.getDictaLibraryBooks()
    this.$nextTick(() => {
      this.formattedText()
    })
  },
  computed: {
    textForApiCall() {
      return this.$store.state.textForApiCall
    },
    hebrew() {
      return this.$settings.hebrew
    },
    running() {
      return (
        this.$store.state.serverState === RunStates.RUNNING ||
        this.$store.state.synopsisServerState === RunStates.RUNNING
      )
    },
    results() {
      return _.groupBy(this.sortedData, (item) => item.baseMatchedText)
    },
    data() {
      return this.$store.state.data.map((item) => {
        if (item.compBookXmlId)
          item.year = parseInt(this.itemMetaData(item.compBookXmlId))
        else item.year = "NA"
        return item
      })
    },
    identified() {
      let identifiedResults = this.data.filter((item) => {
        return item.identified > 90
      })
      let maxVal = identifiedResults.reduce(
        (acc, res) => (acc = acc > res.identified ? acc : res),
        0
      )
      if (identifiedResults.length > 0)
        return {
          book: maxVal.compNameHe.replace('קורפוס עממ"י, ', ""),
          val: maxVal.identified,
        }
      return null
    },
    allDataForMeta() {
      let sortedResults = this.data.filter(
        //filter also by compBookXmlId
        (item) => !item.identified || item.identified < 90
      )
      //item => ((item.year >= this.range.fromYear &&  item.year <= this.range.toYear) || item.year === 'NA' || item.year === 'null')  && ( !item.identified || item.identified < 90))//[0].data;
      sortedResults = sortedResults.sort((a, b) =>
        parseInt(a.year) > parseInt(b.year) ? 1 : -1
      )
      return _.groupBy(sortedResults, (item) => item.baseMatchedText)
    },
    sortedData() {
      let sortedResults = this.data.filter(
        //filter also by compBookXmlId
        (item) => !item.identified || item.identified < 90
      )
      if (this.sourcesToInclude.length > 0) {
        sortedResults = sortedResults.filter((item) =>
          this.sourcesToInclude.includes(
            item.compBookXmlId.replace("benyehuda.", "")
          )
        )
      }
      //item => ((item.year >= this.range.fromYear &&  item.year <= this.range.toYear) || item.year === 'NA' || item.year === 'null')  && ( !item.identified || item.identified < 90))//[0].data;
      //sortedResults = sortedResults.sort((a, b) => (a.year > b.year ? 1 : -1))
      return sortedResults
    },
    serverErrorMessage() {
      return this.hebrew
        ? "לא ניתן להציג את החלון להשוואת גרסאות"
        : "Unable to display the Synopsis screen"
    },
    synopsisComplete() {
      return this.$store.state.synopsisServerState === RunStates.COMPLETE
    },
    failed: {
      get() {
        return (
          this.$store.state.synopsisServerState === RunStates.FAILED ||
          this.$store.state.synopsisServerState === RunStates.TIMED_OUT
        )
      },
      set() {
        this.$store.commit("FOOTNOTES_NOT_RUN")
        // this.synopsisFileIndex = -1
      },
    },
  },
  watch: {
    sortedData() {
      this.formattedText()
    },
    data() {
      this.formattedText()
    },
    synopsisComplete(val) {
      if (val) {
        this.showSynopsis = true
      }
    },
  },
  methods: {
    setFilterValues(IDs) {
      this.sourcesToInclude = IDs
      //  this.sourcesToExclude = data.hiddenIds
    },
    getDictaLibraryBooks() {
      const axiosInstance = axios.create({ timeout: 60000 })
      const LIBRARY_SERVER = "https://books-json-june-2025.dicta-library-data.pages.dev"
      axiosInstance
        .get(LIBRARY_SERVER + "/books.json")
        .then((response) => {
          this.dictaLibraryBooks = response.data
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    sourceClicked(item) {
      if (item.compBookXmlId.includes("dicta-ocr")) {
        let sourceChapter = item.url.split("book/")[1].split("/")[1]
        let bookName = sourceChapter.split("-")[0]
        console.log('bookName:')
        console.log(bookName)
        let index = this.dictaLibraryBooks
          .map(function (e) {
            return e.fileName
          })
          .indexOf(bookName)
        if (index >= 0) {
          let libraryUrl =
            "https://library.dicta.org.il/book/" +
            bookName +
            "/" +
            sourceChapter
          window.open(libraryUrl)
        }
      } else {
        let itemForChapter = {
          ...item,
        }
        if (itemForChapter.compMatchedText.split(" ").length > 300) {
          itemForChapter.compMatchedText =
            "..." +
            itemForChapter.compMatchedText.substring(
              itemForChapter.compStartChar - 800,
              itemForChapter.compStartChar
            )
          itemForChapter.compMatchedText +=
            "<b>" +
            item.compMatchedText.substring(
              item.compStartChar,
              item.compStartChar + item.compTextLength
            ) +
            "</b>"
          itemForChapter.compMatchedText +=
            " " +
            item.compMatchedText.substring(
              item.compStartChar + item.compTextLength,
              item.compStartChar + item.compTextLength + 800
            ) +
            "..."
        } else {
          itemForChapter.compMatchedText =
            "..." +
            itemForChapter.compMatchedText.substring(
              0,
              itemForChapter.compStartChar
            )
          itemForChapter.compMatchedText +=
            "<b>" +
            item.compMatchedText.substring(
              item.compStartChar,
              item.compStartChar + item.compTextLength
            ) +
            "</b>"
          itemForChapter.compMatchedText +=
            " " +
            item.compMatchedText.substring(
              item.compStartChar + item.compTextLength,
              item.compStartChar + item.compTextLength
            ) +
            "..."
        }
        this.itemForChapter = itemForChapter
        this.showFullSource = true
      }
    },
    updateSettings(settingsObj) {
      this.showEarly = settingsObj.early
      this.hideShortCanonical = settingsObj.hideCanocial
      this.formattedText()
    },
    formattedText() {
      this.parallelResults = []
      this.matchedArr = []
      //this.searchedText = this.$store.state.textForApiCall.replace(/\s\s+/g, ' ').split('')
      let tempText = JSON.parse(
        JSON.stringify(this.$store.state.textForApiCall)
      )
      tempText = tempText.replace(/\s\s+/g, " ").replace(/\n/g, " ")
      this.searchedTextArr = tempText.split("")
      if (tempText) {
        const sortedResultsArr = Object.keys(this.results)
        const sortedResultsArrVals = Object.values(this.results)
        if (this.hideShortCanonical) {
          sortedResultsArrVals.forEach((arr, index) => {
            sortedResultsArrVals[index] = arr.filter(
              (item) => !item.ShortCanonical
            )
          })
        }
        if (this.showEarly) {
          sortedResultsArrVals.forEach((arr, index) => {
            let itemsWithYear = arr.filter((item) => parseInt(item.year) > 0)
            let earliestYear = Math.min(
              ...itemsWithYear.map((item) => parseInt(item.year))
            )
            sortedResultsArrVals[index] = itemsWithYear.filter((item) => {
              return (
                parseInt(item.year) >= earliestYear &&
                parseInt(item.year) <= earliestYear + 25
              )
            })
          })
        }

        this.numResults = 0
        sortedResultsArrVals.forEach((arr) => {
          this.numResults += arr.length
        })
        sortedResultsArr.forEach((el, index) => {
          if (sortedResultsArrVals[index].length > 0) {
            let obj = el.replace(/\s\s+/g, " ")
            let startText = tempText.indexOf(obj, 0) //obj.baseStartChar
            let endText = tempText.indexOf(obj, 0) + obj.length //obj.baseStartChar
            this.matchedArr.push({
              sourceIndex: index,
              startPos: startText,
              endPos: endText,
            })
          }
        })
        this.matchedArr = this.matchedArr.sort((a, b) =>
          a.endPos > b.endPos ? 1 : -1
        )
        this.matchedArr.forEach((element, index) => {
          this.parallelResults.push(sortedResultsArrVals[element.sourceIndex])
          this.searchedTextArr[parseInt(element.endPos - 1)] =
            this.searchedTextArr[parseInt(element.endPos - 1)] +
            '<small id="reference-no-' +
            index +
            '" class="text-primary pr-1 cursor-pointer">[' +
            (index + 1) +
            "]</small>"
          this.searchedTextArr[parseInt(element.endPos)]
        })
        this.displayText = this.searchedTextArr.join("")
        this.displayText = this.displayText.replace(/(?:\r\n|\r|\n)/g, "<br>")
      }
    },
    textClicked(event) {
      let scrollSrc = event.target.id.split("-")[2]
      if (scrollSrc !== undefined) {
        document
          .getElementById("match-" + scrollSrc)
          .scrollIntoView({ behavior: "smooth", block: "start" })
        this.findSource(scrollSrc, false)
      }
    },
    itemMetaData(compBookXmlId) {
      let selected
      if (compBookXmlId.includes("benyehuda")) {
        selected = BenYehudaMetaData.filter(function (item) {
          return item.ID === compBookXmlId.replace("benyehuda.", "")
        })
      } else {
        selected = metaData.filter(function (item) {
          return item.compBookXmlId === compBookXmlId
        })
      }
      return selected[0] ? selected[0].Year.replace(".birth", "") : "NA"
    },
    getSingleMetaData(group) {
      this.singleObjectClicked = true
      this.sefariaData = []
      group.map((element) => {
        this.sefariaData.push(element.compBookXmlId.replace("benyehuda.", ""))
      })
      this.showSourcePopup = true
    },
    compareMatches(item) {
      if (item.length == 1) {
        if (
          item[0].baseMatchedText ===
          item[0].compMatchedText.substring(
            item[0].compStartChar,
            item[0].compStartChar + item[0].compTextLength
          )
        )
          return false
      } else {
        var compTextUnique = _.uniqBy(item, "compMatchedText")
        if (
          compTextUnique.length == 1 &&
          compTextUnique[0].compMatchedText.substring(
            compTextUnique[0].compStartChar,
            compTextUnique[0].compStartChar + compTextUnique[0].compTextLength
          ) === compTextUnique[0].baseMatchedText
        )
          return false
      }
      return true
    },
    findSource(index, scrollToPos = true) {
      if (scrollToPos) {
        document
          .getElementById("reference-no-" + index)
          .scrollIntoView({ behavior: "smooth", block: "center" })
      }
      this.activeSrcIndex = index
      let emFirst = this.searchedTextArr.findIndex(
        (element) => element === "<em>"
      )
      if (emFirst >= 0) this.searchedTextArr.splice(emFirst, 1)
      let emLast = this.searchedTextArr.findIndex(
        (element) => element === "</em>"
      )
      if (emLast >= 0) this.searchedTextArr.splice(emLast, 1)
      this.searchedTextArr.splice(this.matchedArr[index].startPos, 0, "<em>")
      this.searchedTextArr.splice(this.matchedArr[index].endPos + 1, 0, "</em>")
      this.displayText = this.searchedTextArr
        .join("")
        .replace(/(?:\r\n|\r|\n)/g, "<br>")
    },

    showSynopsisClicked(data) {
      gtag("event", "Synopsis Clicked")
      this.$store.commit("SET_DATA_FOR_SYNOPSIS", data)
      this.$store.dispatch(Actions.RUN_SYNOPSIS)
    },
    toggleList(index) {
      const selectedList = this.$refs.sourceItem[index]
      selectedList.classList.toggle("show-all")
    },
  },
}
</script>

<style lang="scss">
.identified-text {
  font-size: 12px;
  font-family: "Arimo";
}

.active-src {
  small {
    &.text-muted span {
      background: #d6ecff;
      line-height: 11px;

      &.first-word {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }

      &.last-word {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
    }
  }
}

.searched-text {
  em {
    background: #d6ecff;
    box-shadow: inset 16px 1px 1px 3px #fff;
    // padding: 2px 0;
    position: relative;
    z-index: 2;
    font-style: normal;

    //display: inline;
    &::before,
    &::after {
      content: "";
      position: absolute;
      height: 11x;
      z-index: 1;
      width: 3px;
      right: -3px;
      background-color: #d6ecff;
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
      top: 4px;
    }

    &::after {
      right: auto;
      margin-right: -20px;
      top: auto;
      bottom: 2px;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
  }
}

.raised-frame {
  border-radius: 6px;
}

.open-meta {
  i {
    font-size: 15px;
  }
}

.item-wrap {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
}

.p-group {
  .footnote-num {
    right: 5px;
    font-size: 12px;
    margin-top: 2px;
  }

  border-radius: 6px;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.06);
  border: solid 1px #e6e6e6;

  .border-top-g {
    border-top: 1px solid #d8d8d8 !important;
  }

  .border-bottom-g {
    border-bottom: 1px solid #dedede !important;
  }

  .btns {
    height: 30px;
    font-size: 12px;

    .text-muted {
      line-height: 30px;
    }
  }

  .synopsis-btn,
  .open-meta-single {
    font-size: 12px;
    height: 24px;
    margin-top: 3px;

    .rounded-circle {
      height: 15px;
      width: 15px;
      overflow: hidden;
      font-size: 7px;
      vertical-align: middle;
      line-height: 15px;
      margin-right: -18px;
      margin-left: -18px;
      margin-top: 1px;
      background: black;
    }
  }

  .open-meta-single {
    i {
      font-size: 7px;
    }
  }

  .fas {
    font-size: 20px;
  }

  .read-less {
    display: none;
  }

  .inner-results {
    display: none;
  }
}

.read-less,
.read-more {
  width: 28px;
  height: 28px;
  line-height: 33px;
}

.show-all {
  .read-less {
    display: inline-block !important;
    line-height: 30px;
  }

  .read-more {
    display: none;
  }

  .inner-results {
    display: block !important;
  }
}

.cursor-pointer {
  cursor: pointer;
}
</style>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
.footnote-display {
  button {
    min-width: 32px;
  }

  .f-narkis {
    font-size: 14px;
  }

  direction: rtl;

  .rounded-top {
    direction: ltr;
  }

  .text-col {
    height: calc(100vh - 160px);

    @media screen and (max-width: 1240px) {
      height: auto !important;
    }

    // direction: rtl;
    text-align: right;
    overflow-y: auto;
  }
}

.he .footnote-display .rounded-top {
  direction: rtl;
}
</style>
